import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { useCMS } from '../../context/CMSContext';
import { Save, Sparkles, RefreshCw, Plus, Trash2, GripVertical, Edit2, X, Check } from 'lucide-react';

const AdminAIFeaturesSettingsPage = () => {
  const { 
    getAIFeaturesSettings, updateAIFeaturesSettings,
    getAIFeatures, createAIFeature, updateAIFeature, deleteAIFeature 
  } = useCMS();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    hero_title: '',
    hero_subtitle: '',
    hero_badge: '',
    stats: [],
    cta_title: '',
    cta_subtitle: '',
    cta_button_text: ''
  });
  const [features, setFeatures] = useState([]);
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeature, setNewFeature] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [settingsData, featuresData] = await Promise.all([
        getAIFeaturesSettings(),
        getAIFeatures(false)
      ]);
      if (settingsData) setSettings(settingsData);
      if (featuresData) setFeatures(featuresData);
    } catch (error) {
      console.error('Failed to load AI Features data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await updateAIFeaturesSettings(settings);
      toast.success('AI Features settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleStatChange = (index, field, value) => {
    const newStats = [...settings.stats];
    newStats[index] = { ...newStats[index], [field]: value };
    setSettings(prev => ({ ...prev, stats: newStats }));
  };

  const addStat = () => {
    setSettings(prev => ({
      ...prev,
      stats: [...prev.stats, { value: '', label: '' }]
    }));
  };

  const removeStat = (index) => {
    setSettings(prev => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index)
    }));
  };

  // Feature CRUD
  const handleCreateFeature = async () => {
    try {
      await createAIFeature({
        ...newFeature,
        order: features.length
      });
      toast.success('Feature created');
      setNewFeature(null);
      loadData();
    } catch (error) {
      toast.error('Failed to create feature');
    }
  };

  const handleUpdateFeature = async (id) => {
    try {
      await updateAIFeature(id, editingFeature);
      toast.success('Feature updated');
      setEditingFeature(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update feature');
    }
  };

  const handleDeleteFeature = async (id) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;
    try {
      await deleteAIFeature(id);
      toast.success('Feature deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete feature');
    }
  };

  const handleToggleActive = async (feature) => {
    try {
      await updateAIFeature(feature.id, { is_active: !feature.is_active });
      toast.success(`Feature ${feature.is_active ? 'hidden' : 'shown'}`);
      loadData();
    } catch (error) {
      toast.error('Failed to toggle feature');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin h-8 w-8 text-tech-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-primary-navy">AI Features Page Settings</h1>
          <p className="text-gray-500">Configure the AI features page content and feature list</p>
        </div>
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-tech-blue hover:bg-tech-blue/90">
          {saving ? <RefreshCw className="animate-spin mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
          Save Settings
        </Button>
      </div>

      {/* Hero Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-tech-blue" />
            Hero Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Hero Badge</label>
            <Input
              value={settings.hero_badge}
              onChange={(e) => handleChange('hero_badge', e.target.value)}
              placeholder="Powered by Advanced AI"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Title</label>
            <Input
              value={settings.hero_title}
              onChange={(e) => handleChange('hero_title', e.target.value)}
              placeholder="AI That Actually Helps NDIS Providers"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Hero Subtitle</label>
            <textarea
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-tech-blue/20"
              rows={3}
              value={settings.hero_subtitle}
              onChange={(e) => handleChange('hero_subtitle', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats Section */}
      <Card>
        <CardHeader>
          <CardTitle>Stats Display</CardTitle>
          <CardDescription>Configure the statistics shown on the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {settings.stats?.map((stat, index) => (
            <div key={index} className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Value</label>
                <Input
                  value={stat.value}
                  onChange={(e) => handleStatChange(index, 'value', e.target.value)}
                  placeholder="60%"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Label</label>
                <Input
                  value={stat.label}
                  onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                  placeholder="Less Admin Time"
                />
              </div>
              <Button variant="ghost" onClick={() => removeStat(index)} className="text-red-500">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button variant="outline" onClick={addStat}>
            <Plus className="h-4 w-4 mr-2" /> Add Stat
          </Button>
        </CardContent>
      </Card>

      {/* AI Features List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>AI Features</span>
            <Button
              size="sm"
              onClick={() => setNewFeature({ title: '', description: '', icon: 'Sparkles', benefits: [], is_active: true })}
            >
              <Plus className="h-4 w-4 mr-2" /> Add Feature
            </Button>
          </CardTitle>
          <CardDescription>Manage individual AI features displayed on the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New Feature Form */}
          {newFeature && (
            <div className="p-4 border-2 border-dashed border-tech-blue rounded-lg space-y-3 bg-tech-blue/5">
              <div className="flex justify-between items-center">
                <span className="font-medium text-tech-blue">New Feature</span>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreateFeature} className="bg-tech-blue">
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewFeature(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Input
                  placeholder="Feature Title"
                  value={newFeature.title}
                  onChange={(e) => setNewFeature({ ...newFeature, title: e.target.value })}
                />
                <Input
                  placeholder="Icon (e.g., Shield)"
                  value={newFeature.icon}
                  onChange={(e) => setNewFeature({ ...newFeature, icon: e.target.value })}
                />
              </div>
              <textarea
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Feature description..."
                value={newFeature.description}
                onChange={(e) => setNewFeature({ ...newFeature, description: e.target.value })}
              />
            </div>
          )}

          {/* Existing Features */}
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className={`p-4 border rounded-lg ${!feature.is_active ? 'bg-gray-100 opacity-60' : 'bg-white'}`}
            >
              {editingFeature?.id === feature.id ? (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="font-medium">Editing</span>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdateFeature(feature.id)} className="bg-tech-blue">
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingFeature(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    <Input
                      value={editingFeature.title}
                      onChange={(e) => setEditingFeature({ ...editingFeature, title: e.target.value })}
                    />
                    <Input
                      value={editingFeature.icon}
                      onChange={(e) => setEditingFeature({ ...editingFeature, icon: e.target.value })}
                    />
                  </div>
                  <textarea
                    className="w-full px-3 py-2 border rounded-lg"
                    value={editingFeature.description}
                    onChange={(e) => setEditingFeature({ ...editingFeature, description: e.target.value })}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <div>
                      <h4 className="font-medium">{feature.title}</h4>
                      <p className="text-sm text-gray-500">{feature.description?.slice(0, 60)}...</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleActive(feature)}
                      className={feature.is_active ? 'text-green-600' : 'text-gray-400'}
                    >
                      {feature.is_active ? 'Active' : 'Hidden'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingFeature(feature)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteFeature(feature.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card>
        <CardHeader>
          <CardTitle>Call to Action Section</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">CTA Title</label>
            <Input
              value={settings.cta_title}
              onChange={(e) => handleChange('cta_title', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Subtitle</label>
            <Input
              value={settings.cta_subtitle}
              onChange={(e) => handleChange('cta_subtitle', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">CTA Button Text</label>
            <Input
              value={settings.cta_button_text}
              onChange={(e) => handleChange('cta_button_text', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAIFeaturesSettingsPage;
